'use client';

import * as React from 'react';
import { useMemo, useState } from 'react';
import { PiCaretDown } from 'react-icons/pi';
import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';
import * as FlagComponents from 'country-flag-icons/react/3x2';
import { Button } from './Button';
import { Popover, PopoverContentNoPortal, PopoverTrigger } from './Popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './Command';
import { cn } from '@ais-ui/utils';

export interface PhoneCountry {
  iso: CountryCode;
  name: string;
  dialCode: string;
}

const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : undefined;

/**
 * All countries libphonenumber-js has a calling code for, with a
 * human-readable English name attached. Computed once at module load —
 * this data never changes at runtime.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = getCountries()
  .map((iso) => ({
    iso,
    name: regionNames?.of(iso) ?? iso,
    dialCode: getCountryCallingCode(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const FLAG_COMPONENTS = FlagComponents as unknown as Record<
  string,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
>;

/**
 * Small rectangular (3:2) SVG country flag. Renders a muted placeholder
 * square for any ISO code `country-flag-icons` doesn't have (e.g. an
 * unrecognized/legacy code), so a missing flag never breaks the layout.
 */
export function CountryFlag({
  iso,
  className,
}: {
  iso: string;
  className?: string;
}) {
  const Flag = FLAG_COMPONENTS[iso];

  if (!Flag) {
    return (
      <span
        className={cn('inline-block rounded-[2px] bg-muted', className)}
        aria-hidden="true"
      />
    );
  }

  return (
    <Flag
      className={cn('rounded-[2px] ring-1 ring-inset ring-black/10', className)}
      aria-hidden="true"
    />
  );
}

const COUNTRIES_BY_ISO = new Map(
  PHONE_COUNTRIES.map((country) => [country.iso, country]),
);

function matchesCountry(country: PhoneCountry, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;

  const dialQuery = trimmed.replace(/^\+/, '');
  return (
    country.name.toLowerCase().includes(trimmed) ||
    country.iso.toLowerCase().includes(trimmed) ||
    country.dialCode.includes(dialQuery)
  );
}

// Filtering through cmdk (rather than unmounting non-matching items) keeps
// every item registered, so narrowing the list doesn't pay cmdk's per-item
// unregister cost for ~245 countries on each keystroke.
function filterCountry(iso: string, search: string): number {
  const country = COUNTRIES_BY_ISO.get(iso as CountryCode);
  return country && matchesCountry(country, search) ? 1 : 0;
}

export interface CountryCodeSelectProps {
  value: CountryCode;
  onChange: (iso: CountryCode) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Searchable country + dial-code picker, styled as the left segment of a
 * split phone field: `[flag] [ISO] +[dial code] caret`.
 */
export function CountryCodeSelect({
  value,
  onChange,
  disabled,
  className,
}: CountryCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => PHONE_COUNTRIES.find((country) => country.iso === value),
    [value],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={
            selected
              ? `Select country code, currently ${selected.name} +${selected.dialCode}`
              : 'Select country code'
          }
          className={cn(
            'gap-1.5 px-2.5 rounded-r-none border-r-0 shadow-none font-normal',
            className,
          )}
        >
          <CountryFlag
            iso={selected?.iso ?? value}
            className="w-[18px] h-[13px] flex-none"
          />
          <span className="font-medium">{selected?.iso ?? value}</span>
          <span className="text-muted-foreground tabular-nums">
            +{selected?.dialCode}
          </span>
          <PiCaretDown className="opacity-55 size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal
        align="start"
        className="w-[288px] p-0 overflow-hidden"
      >
        <Command filter={filterCountry}>
          <CommandInput
            placeholder="Search country or code"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-56">
            <CommandEmpty className="text-xs text-muted-foreground py-3">
              No country matches that.
            </CommandEmpty>
            {PHONE_COUNTRIES.map((country) => (
              <CommandItem
                key={country.iso}
                value={country.iso}
                onSelect={() => {
                  onChange(country.iso);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <CountryFlag
                  iso={country.iso}
                  className="w-[18px] h-[13px] flex-none"
                />
                <span className="text-[11px] font-semibold w-5 flex-none text-muted-foreground">
                  {country.iso}
                </span>
                <span className="flex-1 truncate">{country.name}</span>
                <span className="text-muted-foreground tabular-nums">
                  +{country.dialCode}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
}
